import axios from "axios";
import {environmentConfig} from "@/config/enviromentConfig";
import {triviaClient} from "@/api/client";
import {getFingerprintRequestId} from "@/functions/fingerprint";
import {getCaptchaToken, WIDGET_ID} from "@/functions/turnstile";

async function captchaHeaders(action) {
    const token = await getCaptchaToken(action);

    return token ? {"X-Captcha-Token": token, "X-Captcha-Action": action, "X-Captcha-Widget": WIDGET_ID} : {};
}

export default class DepService {
    static async trackLogin(msisdn, productId, service) {
        try {
            return await axios.post(`${environmentConfig.depService}/track-user-login`, {
                    msisdn,
                    productId,
                    serviceId: service,
                    isWeb: 1,
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async trackLoginV2(encryptedMsisdn, serviceId) {
        try {
            return await axios.post(`${environmentConfig.depService}/track-login/v2`, {
                    encryptedMsisdn,
                    service: serviceId,
                    isWeb: 1,
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async doiOne(msisdn, service, productId) {
        return await axios.post(`${environmentConfig.depService}/upsell/doi-one`, {
                msisdn,
                serviceId: service,
                productId,
                externalRef: 1234,
                channel: 'WAP',
                doiChannel: "WAP_REDIRECT"
            },
        )
    }

    static async doiTwo(msisdn, service,subscriptionId) {
        return await axios.post(`${environmentConfig.depService}/upsell/doi-two`, {
                msisdn,
            subscriptionId,
                serviceId: service,
                origination: "Fanclash - Web",
            },
        )
    }

    static async requestOtp(msisdn, productId) {
        const headers = await captchaHeaders("request_otp");
        const config = Object.keys(headers).length ? {headers} : undefined;

        return await triviaClient.post(`/dep/send-otp`, {
            msisdn,
            productId,
        }, config)
    }

    static async validateOtp(msisdn, productId, otp) {
        const fingerprintRequestId = await getFingerprintRequestId();
        const headers = {
            ...(fingerprintRequestId ? {"X-Fingerprint-Request-Id": fingerprintRequestId} : {}),
            ...(await captchaHeaders("submit_otp")),
        };
        const config = Object.keys(headers).length ? {headers} : undefined;

        return await triviaClient.post(`/dep/validate-otp`, {
            msisdn,
            productId,
            otp,
        }, config)
    }

    static async subscribeUser(msisdn, serviceId, subscriptionId, productId, campaignId = 0, clickId) {
        return await axios.post(`${environmentConfig.depService}/upsell/subscribe`, {
                msisdn,
                serviceId,
                subscriptionId,
                productId,
                campaignId,
                clickId
            },
        )
    }

    static async subscribeEncryptedUser(encryptedMsisdn, token, campaignId, evinaToken, contentOverride = undefined, googleClickId, serviceId = 'vc-fanclash-beatthejams-01') {
        try {
            return await axios.post(`${environmentConfig.depService}/subscribe/v2`, {
                    encryptedMsisdn: encryptedMsisdn,
                    token: token,
                    serviceId: serviceId,
                    "externalRef": 12335642,
                    revenuePartnerId: 33,
                    campaignId: campaignId,
                    acquisitionChannel: 'Organic-Web',
                    evinaToken: evinaToken,
                    contentOverride: contentOverride,
                    googleClickId
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async trackUserUpgrade(msisdn, productId, oldService, newService) {
        return await axios.post(`${environmentConfig.depService}/track-multi-tier-upgrade`, {
                msisdn,
                productId,
                oldService,
                newService,
            },
        )
    }

    static async trackUserUpgradeV2(encryptedMsisdn, productId, oldService, newService) {
        return await axios.post(`${environmentConfig.depService}/track-multi-tier-upgrade/v2`, {
                encryptedMsisdn: encryptedMsisdn,
                productId,
                oldService,
                newService,
            },
        )
    }

    static async unSubscribeUser(msisdn,productId, subscriptionId ) {
        return await axios.post(`${environmentConfig.depService}/mzansi-rhythm/unsubscribe`, {
                msisdn,
                productId,
                subscriptionId
            }, {
                headers: {
                    Authorization: `Basic ${environmentConfig.authHeaderUnsubscribe}`
                }
            }
        )
    }

    static async unsubscribeEncryptedUser(encryptedMsisdn, serviceId, isDcbOnly) {
        try {
            return await axios.post(`${environmentConfig.depService}/unsubscribe/v2`, {
                    encryptedMsisdn,
                    serviceId: serviceId,
                    "externalRef": 12335642,
                    isDcbOnly: isDcbOnly
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async getUserViaProductId(msisdn, productId) {
        try {
            return await axios.post(`${environmentConfig.depService}/get-user-via-msisdn`, {
                    msisdn,
                    productId: productId,
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async validateMyBundleEntry(productId, rawQueryString) {
        const fingerprintRequestId = await getFingerprintRequestId();
        const headers = fingerprintRequestId ? {"X-Fingerprint-Request-Id": fingerprintRequestId} : {};
        const config = Object.keys(headers).length ? {headers} : undefined;
        const response = await triviaClient.post(`/dep/my-bundle/validate-entry`, {
            productId,
            rawQueryString,
        }, config)

        return response.data;
    }

    static async getUserViaEncryptedMsisdn(encryptedMsisdn, token, serviceId) {
        try {
            return await axios.post(`${environmentConfig.depService}/get-user-via-encrypted-msisdn/v2`, {
                    encryptedMsisdn: encryptedMsisdn,
                    token: token,
                    serviceId: serviceId,
                    externalRef: 1233564,
                },
            )
        } catch (error) {
            return {authError: true};
        }
    }

    static async createUsername(msisdn, productId, username) {
        return await axios.post(`${environmentConfig.depService}/update-username`, {
                msisdn,
                productId,
                username
            },
        )
    }

    static async updateAvatarFromMsisdn(msisdn, productId, avatar) {
        return await axios.post(`${environmentConfig.depService}/update-avatar-from-msisdn`, {
                msisdn,
                productId,
                avatar
            },
        )
    }

    static async decryptMsisdn(encryptedMsisdn) {
        const fingerprintRequestId = await getFingerprintRequestId();
        const headers = {
            ...(fingerprintRequestId ? {"X-Fingerprint-Request-Id": fingerprintRequestId} : {}),
            ...(await captchaHeaders("decrypt_msisdn")),
        };
        const config = Object.keys(headers).length ? {headers} : undefined;
        const response = await triviaClient.post(`/encryption/decrypt-msisdn`, {
            encryptedMsisdn,
        }, config)

        return response.data;
    }

    static async addFunds(msisdn, productId, gameFundsTypeId, gameFundsAllocationTypeId, value, fromGameId) {
        return await axios.post(`${environmentConfig.depService}/add-game-funds`, {
            msisdn,
            productId,
            gameFundsTypeId,
            gameFundsAllocationTypeId,
            value,
            fromGameId
        })
    }

    static async encryptUserMsisdn(msisdn) {
        return await axios.post(`${environmentConfig.depService}/encrypt-msisdn/v2`, {
                msisdn: msisdn,
            },
        )
    }

}
